import React from "react";
import { faFlag, faHandHoldingDollar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Feedback from "$components/feedback";
import CollapsingPills from "$components/table/cells/collapsing-pills";
import LargeNumberRange from "$components/table/cells/large-number-range";
import LogoCell from "$components/table/cells/logo";
import PredefinedNumberRange from "$components/table/cells/predefined-number-range";
import ProbabilityBubble from "$components/probability";
import { MultiRangeFilter, ProbabilityRange } from "$components/table/components/filters/filter";
import { getTerm } from "$stores/dictionary";
import { closeModal, initiateModal } from "$stores/modal";

export const buildDefaultColumns = ({ onShortlist, onRemoveShortlist, onDismiss, onUndismiss, onFlagged, globalFilterRanges }) => [
    {
        Header: () => getTerm("projectType"),
        accessorKey: "name",
        id: "name",
        size: 120,
    },
    {
        Header: () => getTerm("parentClient"),
        accessorKey: "clientGroupName",
        id: "clientGroupName",
        size: 160,
        Cell: ({ data, value }) => <LogoCell logoUrl={data?.clientLogoUrl} value={value} />,
    },
    {
        Header: () => getTerm("subClient"),
        accessorKey: "clientName",
        id: "clientName",
        size: 140,
    },
    {
        Header: () => getTerm("serviceLine"),
        accessorKey: "serviceLine",
        id: "serviceLine",
        size: 120,
        enableSorting: false,
        Cell: ({ data, value }) => {
            const items = value ? [{ id: value, text: value }] : [];
            return <CollapsingPills data={items} maxVisible={2} keyPrefix="serviceLine" analyticsLabel={getTerm("serviceLines")} />;
        },
    },
    {
        Header: () => getTerm("industry"),
        accessorKey: "industries",
        id: "industries",
        size: 150,
        enableSorting: false,
        Cell: ({ data, value }) => {
            const pills = (value || []).map((i) => ({ id: i.id, text: i.text }));
            return <CollapsingPills data={pills} maxVisible={2} keyPrefix="industry" analyticsLabel={getTerm("industries")} />;
        },
    },
    {
        Header: () => getTerm("projectTypeGroup"),
        accessorKey: "practiceGroups",
        id: "practiceGroups",
        size: 150,
        enableSorting: false,
        Cell: ({ data, value }) => {
            const pills = (value || []).map((i) => ({ id: i.id, text: i.text }));
            return <CollapsingPills data={pills} maxVisible={2} keyPrefix="practiceGroup" analyticsLabel={getTerm("projectTypeGroups")} />;
        },
    },
    {
        Header: () => "Tags",
        accessorKey: "tags",
        id: "tags",
        size: 120,
        enableSorting: false,
        Cell: ({ data }) => {
            const tags = [];
            if (data.requiresIndependence) {
                tags.push({ id: "independence", text: "Check Conflicts", modifierClassNames: ["-independence"] });
            }
            if (data.isCrossSell) {
                tags.push({ id: "cross-sell", text: "Cross-Sell", icon: faHandHoldingDollar, modifierClassNames: ["-importance"] });
            }
            return <CollapsingPills data={tags} maxVisible={2} keyPrefix="tags" analyticsLabel="Tags" />;
        },
    },
    {
        Header: () => `${getTerm("subClient")} Size`,
        accessorKey: "clientSize",
        id: "clientSizeSortOrder",
        sortDescFirst: true,
        size: 100,
        Cell: ({ data, value }) => <PredefinedNumberRange value={value} isHealthy={true} />,
    },
    {
        Header: () => getTerm("averageRevenue"),
        accessorKey: "averageRevenue",
        id: "averageRevenue",
        sortDescFirst: true,
        size: 120,
        Filter: MultiRangeFilter,
        filters: {
            min: globalFilterRanges?.averageRevenueMin ? Math.floor(globalFilterRanges.averageRevenueMin) : "",
            max: globalFilterRanges?.averageRevenueMax ? Math.ceil(globalFilterRanges.averageRevenueMax) : "",
            format: { isCurrency: true },
        },
        Cell: ({ data, value }) => <LargeNumberRange value={value} decimalPrecision={0} />,
    },
    {
        Header: () => "Probability",
        accessorKey: "probability",
        id: "probability",
        sortDescFirst: true,
        size: 100,
        Filter: ProbabilityRange,
        filters: {
            min: 1,
            max: 3,
            format: { isCurrency: false },
        },
        Cell: ({ data, value }) => <ProbabilityBubble value={value} />,
    },
    {
        Header: () => "Partner",
        accessorKey: "crmName",
        id: "crmName",
        enableSorting: false,
        size: 120,
    },
    {
        Header: () => "",
        accessorKey: "opportunityStatus",
        id: "opportunityStatus",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ data, value }) => {
            const isShortlisted = value === "shortlisted";
            const isDismissed = value === "dismissed";
            if (isDismissed) {
                return (
                    <div className="curate-row__actions" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="button -secondary -small"
                            onClick={() => onUndismiss(data.projectKey)}
                        >
                            Un-dismiss
                        </button>
                    </div>
                );
            }
            return (
                <div className="curate-row__actions" onClick={(e) => e.stopPropagation()}>
                    {isShortlisted ? (
                        <button
                            className="button -secondary -small"
                            onClick={() => onRemoveShortlist(data.projectKey)}
                        >
                            ✓ Shortlisted
                        </button>
                    ) : (
                        <button
                            className="button -primary -small"
                            onClick={() => onShortlist(data.projectKey)}
                        >
                            + Shortlist
                        </button>
                    )}
                    <button
                        className="button -secondary -small"
                        onClick={() => onDismiss(data.projectKey, null)}
                    >
                        Dismiss
                    </button>
                    {data.userFlagged ? (
                        <FontAwesomeIcon icon={faFlag} className="curate-row__flag-indicator" title="Flagged" />
                    ) : (
                        <button
                            className="button -secondary -small -icon"
                            title="Flag"
                            onClick={() => {
                                initiateModal(
                                    Feedback,
                                    {
                                        id: data.id,
                                        projectKey: data.projectKey,
                                        onSubmit: () => {
                                            closeModal();
                                            onFlagged(data.projectKey);
                                        },
                                        analyticsLabel: getTerm("potentialProject"),
                                    },
                                    { analyticsLabel: "Flag" }
                                );
                            }}
                        >
                            <FontAwesomeIcon icon={faFlag} />
                        </button>
                    )}
                </div>
            );
        },
    },
];
